'use strict';

/**
 * @ngdoc function
 * @name 360StoreAngularApp.controller:MyLicensesCtrl
 * @description
 * # MyLicensesCtrl
 * Controller of the 360StoreAngularApp
 */
angular.module('360StoreAngularApp')
	.controller('MyLicensesCtrl', function ($scope, $location, $filter, $uibModal, api, session, storeUtils, BUILD_DATE, BILLING_PERIODS, /**ManagedLicenseSummary[]*/licenses, user) { // Inject resolved 'licenses'
		$scope.user = user;
		$scope.today = storeUtils.currentDate();
		$scope.gracePeriodEnd = storeUtils.addDate($scope.today, -30);
		$scope.tmp = {searchText: ''};
		/*
		///////////////////////////////////////////////////////////////////////////
		// INIT
		///////////////////////////////////////////////////////////////////////////
		*/
		licenses.forEach(licenseSummary => licenseSummary.rootCartItem = licenseSummary.cartItemsById[licenseSummary.rootCartItemId]);

		/**
		 * @type {ManagedLicenseSummary[]}
		 */
		$scope.licenses = licenses; // The array of ManagedLicenseSummary objects from the resolve
		$scope.sortModel = {key: 'firstOrderDate', descending: true}; // Default sort

		$scope.isMaintenance = function (item) {
			// VersionUpdates licenses don't have maintenance/auto-renew, so hide renewal date & auto-renew status for them
			return !!item.maxReleaseDate && item.rootCartItem && item.rootCartItem.licenseType !== 'VersionUpdates';
		}

		/**
		 * @param item
		 * @return {'Failed'|'Enabled'|'Disabled'}
		 */
		$scope.getAutoRenewStatus = function (item) {
			return !$scope.isMaintenance(item) ? '' : item.autoRenewFailed ? 'Failed' : item.autoRenewEnabled ? 'Enabled' : 'Disabled';
		};

		$scope.getRenewalPrice = function (licenseSummary) {
			return licenseSummary.autoRenewEnabled ? licenseSummary.renewalPrice?.renewalPriceWithAutoRenewDiscount : licenseSummary.renewalPrice?.renewalPrice;
		};

		/**
		 * The renewal price they will actually be charged, after any renewal promo credit.
		 */
		$scope.getRenewalPriceWithPromo = function (licenseSummary) {
			return $scope.getRenewalPrice(licenseSummary) * (100 - (licenseSummary.renewalPromo?.creditPercent || 0)) / 100;
		};

		/**
		 * How much auto-renew saves off the standard renewal price, as a whole number of percent (usually 10).
		 * Derived rather than hardcoded, because the multiplier is per-variant on the server.
		 */
		$scope.autoRenewSavingsPercent = function (licenseSummary) {
			const standard = licenseSummary.renewalPrice?.renewalPrice;
			return standard > 0 ? Math.round((1 - licenseSummary.renewalPrice.renewalPriceWithAutoRenewDiscount / standard) * 100) : 0;
		};

		/**
		 * Explanatory note about auto-renew, or null if there is nothing useful to say.
		 * Annual subscriptions (MirrorSync) get no auto-renew discount, so for those we talk about uninterrupted
		 * service rather than savings.
		 */
		$scope.autoRenewNote = function (licenseSummary) {
			const enabled = $scope.getAutoRenewStatus(licenseSummary) === 'Enabled';
			const savings = $scope.autoRenewSavingsPercent(licenseSummary);
			if (savings > 0) {
				return enabled
					? `By auto-renewing you are saving ${savings}% over manual renewal.`
					: `Auto-renew to save ${savings}% on your renewal.`;
			}
			if (licenseSummary.rootCartItem?.licenseType === 'AnnualSubscription') {
				return enabled
					? 'Your subscription renews automatically, so your service will not be interrupted.'
					: `Auto-renew to keep your subscription active — ${$scope.productName(licenseSummary)} requires an active subscription to keep running.`;
			}
			return null;
		};

		/**
		 * @param licenseSummary {ManagedLicenseSummary}
		 */
		$scope.productName = (licenseSummary) => {
			return licenseSummary.productName.replace('360Works ', '');
		};

		/**
		 * @param licenseSummary {ManagedLicenseSummary}
		 */
		$scope.toggleAutoRenew = async function (licenseSummary) {
			let newAutoRenewEnabled = $scope.getAutoRenewStatus(licenseSummary) !== 'Enabled';
			if (!window.confirm('Are you sure you want to turn autoRenew ' + (newAutoRenewEnabled ? 'on' : 'off') + '?')) {
				return; // FIX! use a nicer modal confirm here
			}
			console.log('toggleAutoRenew', licenseSummary)
			$scope.support = await api.toggleAutoRenewForCartItemIds(licenseSummary.licenseKey, licenseSummary.registeredTo, [licenseSummary.rootCartItemId], newAutoRenewEnabled);
			licenseSummary.autoRenewEnabled = newAutoRenewEnabled;
			licenseSummary.autoRenewFailed = false; // this was updated on the server
		};

		/**
		 * Only subscriptions can be billed monthly or quarterly; a perpetual license keeps working when maintenance
		 * lapses, so there is nothing to meter. Mirrors CartItem#isSubscription on the server, which enforces it.
		 *
		 * @param licenseSummary {ManagedLicenseSummary}
		 */
		$scope.isSubscription = function (licenseSummary) {
			return licenseSummary.rootCartItem?.licenseType === 'AnnualSubscription';
		};

		/**
		 * @param licenseSummary {ManagedLicenseSummary}
		 * @return {string} the label for the order's current plan
		 */
		$scope.billingPeriodLabel = function (licenseSummary) {
			const option = BILLING_PERIODS.find(o => o.id === (licenseSummary.billingPeriod || 'Annual'));
			return option ? option.label : 'Yearly';
		};

		/**
		 * A multi-period catch-up renewal buys `renewalQty` of these, which is only a year on an annual plan.
		 *
		 * @param licenseSummary {ManagedLicenseSummary}
		 * @param qty {number}
		 * @return {string} "months", "quarters" or "years"
		 */
		$scope.billingPeriodTermNoun = function (licenseSummary, qty) {
			const option = BILLING_PERIODS.find(o => o.id === (licenseSummary.billingPeriod || 'Annual'));
			const noun = option ? option.termNoun : 'year';
			return qty === 1 ? noun : noun + 's';
		};

		/**
		 * Opens the billing period picker for a subscription license, then folds the result back into the list row
		 * so it reflects the new plan and the repriced renewal without a reload. Used by both the Actions menu and
		 * the license details modal, which shares this scope.
		 *
		 * @param licenseSummary {ManagedLicenseSummary}
		 * @return {Promise<ChangedLicense|undefined>} resolves with the change once applied, or with nothing if the picker was dismissed
		 */
		$scope.changeBillingPeriod = function (licenseSummary) {
			return $uibModal.open({
				templateUrl: 'views/modal-change-billing-period.html?v=' + BUILD_DATE,
				controller: 'ModalChangeBillingPeriodCtrl',
				windowClass: 'billing-period-modal',
				resolve: {
					license: () => licenseSummary,
				}
			}).result.then(function (/**ChangedLicense*/changedLicense) {
				licenseSummary.billingPeriod = changedLicense.billingPeriod;
				if (changedLicense.renewalPrice) {
					licenseSummary.renewalPrice = changedLicense.renewalPrice; // price and date-after-renewal both come from here
				}
				toastr.success('This license will be billed ' + $scope.billingPeriodLabel(licenseSummary).toLowerCase()
					+ ' starting at your next renewal.', 'Billing Period Changed');
				return changedLicense;
			}, angular.noop);
		};

		/**
		 * @param licenseSummary {ManagedLicenseSummary}
		 */
		$scope.renewManually = function (licenseSummary) {
			console.log('renewManually', licenseSummary)
			const requestActions = [
				{action: 'Renew', cartItemId: licenseSummary.rootCartItemId, registeredTo: licenseSummary.registeredTo}
			]
			api.createCartAction(requestActions)
				.then(function (action) {
					session.addCartAction(action);
					$location.path('/checkout');
				});

		};

		$scope.addCapabilitiesUrl = (licenseSummary) => {
			if (!licenseSummary.mirrorSyncCapabilities || !licenseSummary.licenseKey || !licenseSummary.registeredTo) {
				return;
			}
			const licenseKey = encodeURIComponent(licenseSummary.licenseKey);
			const registeredTo = encodeURIComponent(licenseSummary.registeredTo);
			return `/mirrorsync?licenseKey=${licenseKey}&registeredTo=${registeredTo}&action=add`;
		};

		$scope.upgradeLicenseUrl = (/**ManagedLicenseSummary*/licenseSummary) => {
			// Always link to the product purchase page (prefilled), so the user can buy a newer version
			// or a more capable variant. The suggested next variant is just a default, when one exists.
			const productCode = licenseSummary.productCode;
			const licenseKey = encodeURIComponent(licenseSummary.licenseKey);
			const registeredTo = encodeURIComponent(licenseSummary.registeredTo);
			let url = `/add-product/${productCode}?licenseKey=${licenseKey}&registeredTo=${registeredTo}`;
			if (licenseSummary.nextBestVariantTypeCode) {
				url += `&variant=${licenseSummary.nextBestVariantTypeCode}`;
			}
			return url;
		};

		$scope.updatePaymentUrl = (/**ManagedLicenseSummary*/licenseSummary) => {
			const licenseKey = encodeURIComponent(licenseSummary.licenseKey);
			return `/update-payment-info/${licenseKey}`;
		};

		$scope.viewLicenseDetails = (licenseSummary) => {
			$uibModal.open({
				templateUrl: 'views/modal-managed-license-summary-details.html?v=' + BUILD_DATE,
				scope: $scope,
				controller: 'ModalManagedLicenseSummaryDetailsCtrl',
				resolve: {
					licenseSummary: () => licenseSummary,
				}
			}).result.catch(() => {
			});
		};

		$scope.viewReceipt = function (licenseSummary) {
			// Need a relevant cart ID and the license key from the DTO
			// const cartId = licenseSummary.displayCartItemId || licenseSummary.rootCartItemId;
			// const licenseKey = licenseSummary.licenseKey;
			const cartId = licenseSummary.rootCartItemId; // Placeholder
			const licenseKey = licenseSummary.licenseKey; // Placeholder

			if (!cartId || !licenseKey) {
				toastr.warning('Receipt information not available.');
				return;
			}
			$location.path('/receipt').search({cartId: licenseSummary.rootCartId, licenseKey: licenseKey});
		};

		$scope.openLinkLicenseModal = function () {
			$uibModal.open({
				templateUrl: 'views/modal-license-entry.html?v=' + BUILD_DATE,
				controller: 'ModalLicenseEntryCtrl',
				size:'md',
				resolve: {
					product: () => null
				}
			}).result.then(function (licenseInfo) {
				return api.linkLicense(licenseInfo.licenseKey, licenseInfo.registeredTo)
					.then(function () {
						toastr.success('The license has been successfully linked to your account.', 'License Linked');
						// Broadcast event to trigger a refresh of the license list
						$scope.$emit('$linkedLicense');
					});
			}).catch();
		};

		// Listen for the event to refresh
		$scope.$on('$linkedLicense', function () {
			api.getMyLicenses().then(function (licenses) {
				licenses.forEach(licenseSummary => licenseSummary.rootCartItem = licenseSummary.cartItemsById[licenseSummary.rootCartItemId]);
				$scope.licenses = licenses;
			});
		});

	});
